import React, { createContext, useContext, useEffect, useState } from 'react';
import { db } from '../db/index';
import axiosInstance from '../api/axios';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';

interface SyncContextType {
    isOnline: boolean;
    isSyncing: boolean;
    pendingCount: number;
    lastSyncTime: Date | null;
    forceSync: () => Promise<void>;
}

const SyncContext = createContext<SyncContextType>({
    isOnline: true,
    isSyncing: false,
    pendingCount: 0,
    lastSyncTime: null,
    forceSync: async () => { },
});

export const useSync = () => useContext(SyncContext);

export const SyncProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [isSyncing, setIsSyncing] = useState(false);
    const [pendingCount, setPendingCount] = useState(0);
    const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
    const { isAuthenticated, user } = useAuth();

    // Monitor Network Status and Tab Sync
    useEffect(() => {
        const handleOnline = () => {
            setIsOnline(true);
            // Solo intentar sincronizar si hay sesión iniciada y empresa asociada
            if (isAuthenticated && user?.company?.id) {
                toast.success('Conexión restaurada. Sincronizando...');
                syncPendingActions();
            } else {
                toast.success('Conexión restaurada.');
            }
        };
        const handleOffline = () => {
            setIsOnline(false);
            toast.warning('Modo Offline: Los datos se guardarán localmente.');
        };

        // Escuchar cambios en otras pestañas (como si otra pestaña terminó de sincronizar)
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'parqium_last_sync') {
                updatePendingCount();
                setLastSyncTime(new Date());
            }
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        window.addEventListener('storage', handleStorageChange);

        // Check pending count initially
        updatePendingCount();

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    const updatePendingCount = async () => {
        const count = await db.pendingQueue.count();
        setPendingCount(count);
    };

    const syncPendingActions = async () => {
        // No intentar sincronizar si ya se está sincronizando, no hay conexión
        // o el usuario no está autenticado (evitar toasts de error para no autenticados)
        if (isSyncing || !navigator.onLine || !isAuthenticated || !user?.company?.id) return;

        setIsSyncing(true);

        try {
            // Llamar al endpoint del servidor offline que hace el PUSH/PULL completo
            await axiosInstance.post('/sync/now');

            await updatePendingCount();
            setLastSyncTime(new Date());
            localStorage.setItem('parqium_last_sync', Date.now().toString());

            toast.success('✅ Sincronización completada con éxito');
        } catch (error: any) {
            console.error('💥 Error durante sincronización:', error);
            const errorMessage = error.response?.data?.message || 'Error de conexión';
            toast.error(`❌ Sincronización fallida: ${errorMessage}`);
        } finally {
            setIsSyncing(false);
        }
    };

    return (
        <SyncContext.Provider value={{ isOnline, isSyncing, pendingCount, lastSyncTime, forceSync: syncPendingActions }}>
            {children}
        </SyncContext.Provider>
    );
};
